import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ValidationProgress = ({ progress, onCancel }) => {
  const progressPercentage = progress.total > 0 ? (progress.processed / progress.total) * 100 : 0;

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return '0s';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    
    return `${remainingSeconds}s`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processing':
        return 'text-primary';
      case 'completed':
        return 'text-success';
      case 'failed':
        return 'text-error';
      case 'paused':
        return 'text-warning';
      default:
        return 'text-text-secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing':
        return 'Loader';
      case 'completed':
        return 'CheckCircle';
      case 'failed':
        return 'XCircle';
      case 'paused':
        return 'Pause';
      default:
        return 'Clock';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'processing':
        return 'Processing emails...';
      case 'completed':
        return 'Validation completed';
      case 'failed':
        return 'Validation failed';
      case 'paused':
        return 'Validation paused';
      default:
        return 'Preparing validation...';
    }
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-6">
      {/* Status Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${
            progress.status === 'processing' ? 'bg-primary/10' : 
            progress.status === 'completed' ? 'bg-success/10' : 
            progress.status === 'failed' ? 'bg-error/10' : 'bg-gray-100'
          }`}>
            <Icon 
              name={getStatusIcon(progress.status)} 
              size={20} 
              className={`${getStatusColor(progress.status)} ${progress.status === 'processing' ? 'animate-spin' : ''}`}
            />
          </div>
          <div>
            <h3 className="text-lg font-medium text-text-primary">
              {getStatusText(progress.status)}
            </h3>
            <p className="text-sm text-text-secondary">
              {progress.processed} of {progress.total} emails processed
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {progress.status === 'processing' && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              iconName="X"
              iconPosition="left"
            >
              Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-text-primary">
            Progress: {Math.round(progressPercentage)}%
          </span>
          {progress.timeRemaining && (
            <span className="text-sm text-text-secondary">
              {formatTime(progress.timeRemaining)} remaining
            </span>
          )}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Progress Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center p-3 bg-success/10 rounded-lg">
          <div className="text-2xl font-bold text-success">
            {progress.valid?.toLocaleString() || 0}
          </div>
          <div className="text-xs text-success">Valid</div>
        </div>
        
        <div className="text-center p-3 bg-error/10 rounded-lg">
          <div className="text-2xl font-bold text-error">
            {progress.invalid?.toLocaleString() || 0}
          </div>
          <div className="text-xs text-error">Invalid</div>
        </div>
        
        <div className="text-center p-3 bg-warning/10 rounded-lg">
          <div className="text-2xl font-bold text-warning">
            {progress.risky?.toLocaleString() || 0}
          </div>
          <div className="text-xs text-warning">Risky</div>
        </div>
        
        <div className="text-center p-3 bg-gray-100 rounded-lg">
          <div className="text-2xl font-bold text-text-secondary">
            {progress.unknown?.toLocaleString() || 0}
          </div>
          <div className="text-xs text-text-secondary">Unknown</div>
        </div>
      </div>

      {/* Processing Rate */}
      {progress.processingRate > 0 && (
        <div className="mt-4 flex items-center justify-center space-x-2 text-sm text-text-secondary">
          <Icon name="Zap" size={14} />
          <span>Processing at {Math.round(progress.processingRate)} emails/second</span>
        </div>
      )}
    </div>
  );
};

export default ValidationProgress;